'use client';
import React, { useRef } from "react";
import BenefitColumns from "@/components/ui/BenefitColumns/BenefitColumns";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from 'split-type';
import { useGSAP } from "@gsap/react";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface TextBenefit {
  title: string;
  content: string;
}

interface TextBenefitsSectionProps {
  language?: LanguageType
  text: string
  showLogo: boolean
  benefits: TextBenefit[]
  showShadow?: boolean
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const TextBenefitsSection: React.FC<TextBenefitsSectionProps> = ({
  language = "english",
  text,
  showLogo,
  benefits,
  showShadow = true,
}) => {
  const isLg = useIsLg();
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);

  useGSAP(() => {

    const section = sectionRef.current;
    const textElement = textRef.current;
    const columns = columnsRef.current?.children;
    const logo = logoRef.current;

    if (!section || !textElement || !columns) return;

    if (isLg) {
      // DESKTOP LOGIC
      const splitText = new SplitType(textElement, { types: 'words' });

      // Set initial states
      if (logo && showLogo) {
        gsap.set([logo, columns], { autoAlpha: 0 });
      } else {
        gsap.set([columns], { autoAlpha: 0 });
      }
      gsap.set(splitText.words, {
        autoAlpha: 0,
        filter: 'blur(20px)',
        y: +20,
        force3D: true,
      });

      // Create scrubbed timeline
      const scrubTl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          end: "center center-=10%",
          scrub: true,
        },
        onComplete: () => {
          columnsTl.play()
          scrubTl.kill()
        }
      });

      // Create separate timeline for columns (paused initially)
      const columnsTl = gsap.timeline({ paused: true });

      // Logo animation (if present)
      if (logo && showLogo) {
        scrubTl.to(logo, {
          autoAlpha: 1,
          duration: 1,
          ease: "power2.out",
        });
      }

      // Text animation with blur mask reveal
      scrubTl.to(splitText.words, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power2.out",
        force3D: true,
      });

      // Columns animation (in separate timeline)
      columnsTl.to(columns, {
        autoAlpha: 1,
        stagger: {
          each: 0.15,
          // from: language === "arabic" ? "end" : "start"
          from: "start"
        },
        duration: 1,
        ease: "power3.out",
        force3D: true,
      });

    } else {
      // MOBILE LOGIC

      // Set initial states
      if (logo && showLogo) {
        gsap.set([logo, columns], { autoAlpha: 0 });
      } else {
        gsap.set([columns], { autoAlpha: 0 });
      }
      gsap.set(textElement, {
        autoAlpha: 0,
        filter: 'blur(20px)',
        y: +100,
        force3D: true,
      });

      // Create timeline for logo (if present)
      const logoTl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 90%",
          end: "top 90%",
          scrub: false,
        },
      });


      // Logo animation (if present)
      if (logo && showLogo) {
        logoTl.to(logo, {
          autoAlpha: 1,
          duration: 1,
          ease: "power2.out",
        });
      }

      // Text animation (separate, non-scrubbed)
      gsap.to(textElement, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        ease: "power2.out",
        scrollTrigger: {
          trigger: textElement,
          start: "top 85%",
          toggleActions: "play none none none"
        },
        force3D: true,
      });

      // Columns animation (in separate timeline)
      gsap.to(columns, {
        autoAlpha: 1,
        stagger: {
          each: 0.15,
          // from: language === "arabic" && isLg ? "end" : "start"
          from: "start"
        },
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: columns,
          start: "top 70%",
          toggleActions: "play none none none"
        },
        force3D: true,
      });
    }

  }, { dependencies: [showLogo, text, isLg], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      // py-[2rem]
      // tb:py-[3.5rem]
      className="
      relative
      w-full
      flex items-center justify-center
      px-[1.25rem]
      padding-main-x-desktop

      py-[2rem]
      tb:pt-[3.5rem]
      ">

      {showShadow && (
        <div
          aria-hidden="true"
          className="
            absolute left-0 w-full
            top-0 tb:top-1/2 tb:-translate-y-1/2
            aspect-3/4 tb:aspect-3/2
            z-0
            opacity-[8%] tb:opacity-10
            pointer-events-none
            bg-[url('/Website-Shadow-Overlay.avif')] bg-cover bg-center bg-no-repeat
            mix-blend-darken
          "
        />
      )}

      <div className="
        relative
        z-10
        max-w-[100%]
        container-small
        ">
        <div className="
        flex flex-col items-center justify-center

        gap-[2.5rem]
        lg:gap-[4.5rem]
        ">

          {showLogo &&
            <img
              ref={logoRef}
              src="/Logomark-None.svg"
              alt="Support lockup with no registration"
              className="h-[53px] lg:h-[68px]"
            />
          }
          <div className="flex flex-col justify-center gap-[3.5rem] lg:gap-[6rem]">
            <p
              ref={textRef}
              style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
              className={`
              text-center
              text-text-primary
              text-52-to-28--neue-june
            `}>
              {text}
            </p>
            <div>
              <BenefitColumns
                columnsRef={columnsRef}
                showSubheading={false}
                language={language}
                benefits={benefits}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


export default TextBenefitsSection;
